/**
 * Shared FIT stream parsing, smoothing, and range statistics.
 * Used by WorkoutAnalysisView and Comparison workspace.
 */

export interface StreamPoint {
  timeSeconds: number;
  timestamp: number | null;
  power: number | null;
  heartRate: number | null;
  cadence: number | null;
  fractionalCadence?: number | null;
  cadenceUnit?: 'spm' | 'rpm';
  speed: number | null;
  altitude: number | null;
  temperature?: number | null;
  /** Cumulative distance from FIT record (meters), when present. */
  distanceM?: number | null;
  positionLat: number | null;
  positionLong: number | null;
  paused?: boolean;
}

export interface RangeStats {
  duration: number;
  timeStart: number;
  timeEnd: number;
  power: { min: number; max: number; avg: number } | null;
  heartRate: { min: number; max: number; avg: number } | null;
  cadence: { min: number; max: number; avg: number } | null;
  speed: { min: number; max: number; avg: number } | null;
  altitude: { min: number; max: number; avg: number } | null;
}

/** Map slider 0–100 to moving-average window size (1 = raw). */
export function smoothingWindow(level: number, pointCount: number): number {
  if (level <= 0 || pointCount < 3) return 1;
  const maxWin = Math.max(5, Math.min(800, Math.round(pointCount * 0.08)));
  const t = level / 100;
  return Math.round(3 + t * (maxWin - 3));
}

/** Centered moving average; null gaps are skipped inside the window. */
export function smoothNullableSeries(values: (number | null)[], window: number): (number | null)[] {
  if (window <= 1 || values.length === 0) return values;
  const half = Math.floor(window / 2);
  const out: (number | null)[] = new Array(values.length);
  for (let i = 0; i < values.length; i++) {
    let sum = 0;
    let count = 0;
    const lo = Math.max(0, i - half);
    const hi = Math.min(values.length - 1, i + half);
    for (let j = lo; j <= hi; j++) {
      const v = values[j];
      if (v != null && isFinite(v)) {
        sum += v;
        count++;
      }
    }
    out[i] = count > 0 ? sum / count : null;
  }
  return out;
}

/** Apply smoothing to chart metrics while preserving timestamps for selection/hover. */
export function buildSmoothedStream(stream: StreamPoint[], window: number): StreamPoint[] {
  if (window <= 1) return stream;
  const pwr = smoothNullableSeries(stream.map(p => p.power), window);
  const hr = smoothNullableSeries(stream.map(p => p.heartRate), window);
  const cad = smoothNullableSeries(stream.map(p => p.cadence), window);
  const spd = smoothNullableSeries(stream.map(p => p.speed), window);
  const alt = smoothNullableSeries(stream.map(p => p.altitude), window);
  return stream.map((p, i) => ({
    ...p,
    power: pwr[i],
    heartRate: hr[i],
    cadence: cad[i],
    speed: spd[i],
    altitude: alt[i],
  }));
}

/**
 * Unwrap GET /workout/{id}/data response into a payload for parseStreamData.
 */
export function extractStreamRaw(apiBody: unknown): unknown {
  if (apiBody == null) return null;
  const root = (apiBody as { data?: unknown }).data ?? apiBody;
  if (root == null) return null;

  const inner = (root as { data?: unknown }).data ?? root;
  if (Array.isArray(inner)) return inner;
  if (typeof inner === 'object' && inner !== null) {
    if (Array.isArray((inner as { data?: unknown }).data)) return (inner as { data: unknown[] }).data;
    return inner;
  }
  return null;
}

/**
 * Stream speed → km/h for charts and tooltips.
 * vo2-app-api already converts FIT m/s → km/h in FitWorkoutParser::setUnits and Go convertMetricUnit.
 */
export function normalizeStreamSpeedToKmh(raw: unknown): number | null {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

/**
 * Parse raw stream data into StreamPoint[].
 * Handles PHP array format and Go map-per-metric format.
 */
export function parseStreamData(rawData: unknown): StreamPoint[] {
  if (!rawData) return [];

  if (typeof rawData === 'object' && !Array.isArray(rawData) && Array.isArray((rawData as { data?: unknown }).data)) {
    return parseStreamData((rawData as { data: unknown }).data);
  }

  if (Array.isArray(rawData) && rawData.length) {
    // Bridge/USB FIT imports often omit elapsed_time and only send Unix time_stamp.
    const baseTsRaw = rawData.find(
      (pt) =>
        pt != null
        && typeof pt === 'object'
        && ((pt as Record<string, unknown>).timestamp != null
          || (pt as Record<string, unknown>).time_stamp != null),
    ) as Record<string, unknown> | undefined;
    const baseTs = baseTsRaw
      ? Number(baseTsRaw.timestamp ?? baseTsRaw.time_stamp)
      : NaN;
    const useStampElapsed = Number.isFinite(baseTs) && baseTs > 1e9;

    return rawData.map((pt: Record<string, unknown>, i) => {
      const spd =
        pt.speed != null
          ? normalizeStreamSpeedToKmh(pt.speed)
          : pt.enhanced_speed != null
            ? normalizeStreamSpeedToKmh(pt.enhanced_speed)
            : null;
      const timestamp =
        pt.timestamp != null
          ? Number(pt.timestamp)
          : pt.time_stamp != null
            ? Number(pt.time_stamp)
            : null;
      let timeSeconds: number;
      if (pt.elapsed_time != null) {
        timeSeconds = Number(pt.elapsed_time);
      } else if (pt.time_seconds != null) {
        timeSeconds = Number(pt.time_seconds);
      } else if (useStampElapsed && timestamp != null) {
        timeSeconds = timestamp - baseTs;
      } else {
        timeSeconds = i * 5;
      }
      return {
        timeSeconds,
        timestamp,
        power: pt.power != null ? Number(pt.power) : null,
        heartRate: pt.heart_rate != null ? Number(pt.heart_rate) : null,
        cadence: pt.cadence != null ? Number(pt.cadence) : null,
        fractionalCadence: pt.fractional_cadence != null ? Number(pt.fractional_cadence) : null,
        speed: spd,
        altitude: (pt.altitude ?? pt.enhanced_altitude ?? null) as number | null,
        temperature: pt.temperature != null ? Number(pt.temperature) : null,
        distanceM:
          pt.distance != null
            ? Number(pt.distance)
            : pt.total_distance != null
              ? Number(pt.total_distance)
              : null,
        positionLat: pt.position_lat != null ? Number(pt.position_lat) : null,
        positionLong: pt.position_long != null ? Number(pt.position_long) : null,
        paused: pt.paused === true || pt.paused === 1 || pt.paused === '1',
      };
    });
  }

  if (typeof rawData === 'object' && !Array.isArray(rawData)) {
    const record = rawData as Record<string, unknown>;

    const tsArray: number[] = Array.isArray(record.time_stamp)
      ? record.time_stamp.map(Number)
      : Array.isArray(record.timestamp)
        ? record.timestamp.map(Number)
        : [];

    if (tsArray.length === 0) {
      const tsSet = new Set<number>();
      for (const [key, metric] of Object.entries(record)) {
        if (key === 'time_stamp' || key === 'timestamp') continue;
        if (metric != null && typeof metric === 'object' && !Array.isArray(metric)) {
          for (const k of Object.keys(metric as object)) tsSet.add(Number(k));
        }
      }
      if (tsSet.size === 0) return [];
      tsArray.push(...Array.from(tsSet).sort((a, b) => a - b));
    }

    const getMetric = (name: string, tsKey: string): number | null => {
      const m = record[name];
      if (m != null && typeof m === 'object' && !Array.isArray(m) && (m as Record<string, unknown>)[tsKey] != null) {
        return Number((m as Record<string, unknown>)[tsKey]);
      }
      return null;
    };

    return tsArray.map((ts, i) => {
      const tsKey = String(ts);
      const spd =
        getMetric('speed', tsKey) != null
          ? normalizeStreamSpeedToKmh(getMetric('speed', tsKey))
          : getMetric('enhanced_speed', tsKey) != null
            ? normalizeStreamSpeedToKmh(getMetric('enhanced_speed', tsKey))
            : null;
      const elapsed = getMetric('elapsed_time', tsKey);
      return {
        timeSeconds: elapsed ?? (tsArray.length > 1 && tsArray[0] > 1e9 ? ts - tsArray[0] : i * 5),
        timestamp: ts,
        power: getMetric('power', tsKey),
        heartRate: getMetric('heart_rate', tsKey),
        cadence: getMetric('cadence', tsKey),
        fractionalCadence: getMetric('fractional_cadence', tsKey),
        speed: spd,
        altitude: getMetric('altitude', tsKey) ?? getMetric('enhanced_altitude', tsKey),
        temperature: getMetric('temperature', tsKey),
        distanceM: getMetric('distance', tsKey) ?? getMetric('total_distance', tsKey),
        positionLat: getMetric('position_lat', tsKey),
        positionLong: getMetric('position_long', tsKey),
        paused: getMetric('paused', tsKey) === 1,
      };
    });
  }

  return [];
}

/** Convert API/stream/lap distance values to meters (Go range API uses km; PHP uses m). */
export function normalizeRangeDistanceToMeters(value: unknown, elapsedSec = 0): number | null {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n >= 1000) return n;

  if (elapsedSec <= 0) {
    if (!Number.isInteger(n)) return Math.round(n * 1000);
    return n;
  }

  const speedIfMetersKmh = (n / elapsedSec) * 3.6;
  const speedIfKmKmh = (n * 1000 / elapsedSec) * 3.6;
  const metersOk = speedIfMetersKmh >= 1 && speedIfMetersKmh <= 120;
  const kmOk = speedIfKmKmh >= 1 && speedIfKmKmh <= 120;

  if (metersOk && !kmOk) return n;
  if (kmOk && !metersOk) return Math.round(n * 1000);
  if (!Number.isInteger(n)) return Math.round(n * 1000);
  if (n <= 300 && elapsedSec >= 300) return Math.round(n * 1000);

  return n;
}

/** @deprecated alias — use normalizeRangeDistanceToMeters */
export function normalizeApiRangeDistanceM(value: unknown, elapsedSec = 0): number | null {
  return normalizeRangeDistanceToMeters(value, elapsedSec);
}

/** Human-readable distance — shows meters below 1 km (fixes 0.0 km on short laps). */
export function formatWorkoutDistanceM(meters: number | null | undefined): string {
  if (meters == null || !Number.isFinite(meters) || meters <= 0) return '—';
  if (meters < 1000) return `${Math.round(meters)} m`;
  const km = meters / 1000;
  return `${km >= 10 ? km.toFixed(1) : km.toFixed(2)} km`;
}

/** Distance in a stream index range — cumulative FIT delta, else speed integration. */
export function computeRangeDistanceM(pts: StreamPoint[], startIdx: number, endIdx: number): number | null {
  const a = Math.min(startIdx, endIdx);
  const b = Math.max(startIdx, endIdx);
  const slice = pts.slice(a, b + 1);
  if (slice.length < 2) return null;

  const cumulative = slice
    .map((p) => p.distanceM)
    .filter((v): v is number => v != null && Number.isFinite(v));
  if (cumulative.length >= 2) {
    const delta = Math.max(...cumulative) - Math.min(...cumulative);
    if (delta > 0) {
      const elapsed = slice[slice.length - 1].timeSeconds - slice[0].timeSeconds;
      return normalizeRangeDistanceToMeters(delta, elapsed) ?? delta;
    }
  }

  let totalM = 0;
  for (let i = 1; i < slice.length; i++) {
    const dt = slice[i].timeSeconds - slice[i - 1].timeSeconds;
    const spdKmh = slice[i].speed ?? slice[i - 1].speed;
    if (dt > 0 && spdKmh != null && spdKmh > 0) {
      totalM += (spdKmh / 3.6) * dt;
    }
  }
  return totalM > 0 ? Math.round(totalM) : null;
}

/** Aggregate min/avg/max for a stream index range. */
export function computeRangeStats(pts: StreamPoint[], startIdx: number, endIdx: number): RangeStats | null {
  const slice = pts.slice(startIdx, endIdx + 1);
  if (!slice.length) return null;
  const agg = (arr: (number | null)[]) => {
    const vals = arr.filter((v): v is number => v != null && isFinite(v) && v > 0);
    if (!vals.length) return null;
    return { min: Math.min(...vals), max: Math.max(...vals), avg: vals.reduce((a, b) => a + b, 0) / vals.length };
  };
  return {
    duration: slice[slice.length - 1].timeSeconds - slice[0].timeSeconds,
    timeStart: slice[0].timeSeconds,
    timeEnd: slice[slice.length - 1].timeSeconds,
    power: agg(slice.map(p => p.power)),
    heartRate: agg(slice.map(p => p.heartRate)),
    cadence: agg(slice.map(p => p.cadence)),
    speed: agg(slice.map(p => p.speed)),
    altitude: agg(slice.map(p => p.altitude)),
  };
}

/** Find stream indices for a time range in seconds. */
export function findStreamIndicesForRange(
  stream: StreamPoint[],
  startSec: number,
  endSec: number,
): { startIdx: number; endIdx: number } {
  if (!stream.length) return { startIdx: 0, endIdx: 0 };
  let startIdx = 0;
  let endIdx = stream.length - 1;
  for (let i = 0; i < stream.length; i++) {
    if (stream[i].timeSeconds >= startSec) {
      startIdx = i;
      break;
    }
  }
  for (let i = stream.length - 1; i >= 0; i--) {
    if (stream[i].timeSeconds <= endSec) {
      endIdx = i;
      break;
    }
  }
  if (endIdx < startIdx) endIdx = startIdx;
  return { startIdx, endIdx };
}

/** Map FIT unix timestamps to stream indices (peaks / laps → map highlight). */
export function findStreamIndicesForUnixRange(
  stream: StreamPoint[],
  startUnix: number,
  endUnix: number,
): { startIdx: number; endIdx: number } | null {
  if (!stream.length || !Number.isFinite(startUnix) || !Number.isFinite(endUnix)) return null;

  const lo = Math.min(startUnix, endUnix);
  const hi = Math.max(startUnix, endUnix);

  const indexed = stream
    .map((p, i) => ({ i, ts: p.timestamp }))
    .filter((x): x is { i: number; ts: number } => x.ts != null && Number.isFinite(x.ts));

  if (indexed.length > 0) {
    let startIdx = indexed[0].i;
    let endIdx = indexed[indexed.length - 1].i;

    for (const { i, ts } of indexed) {
      if (ts >= lo) {
        startIdx = i;
        break;
      }
    }
    for (let j = indexed.length - 1; j >= 0; j--) {
      if (indexed[j].ts <= hi) {
        endIdx = indexed[j].i;
        break;
      }
    }
    if (endIdx < startIdx) endIdx = startIdx;
    return { startIdx, endIdx };
  }

  const baseTs = stream[0]?.timestamp;
  if (baseTs != null && baseTs > 1_000_000_000) {
    return findStreamIndicesForRange(stream, lo - baseTs, hi - baseTs);
  }

  return null;
}

/** Extract GPS route as [lat, lng][] from stream (FIT semicircles or WGS84 degrees). */
export function extractGpsRoute(stream: StreamPoint[]): [number, number][] {
  const route: [number, number][] = [];
  for (const p of stream) {
    if (p.positionLat == null || p.positionLong == null) continue;
    const lat =
      Math.abs(p.positionLat) <= 90
        ? p.positionLat
        : p.positionLat * (180 / 2 ** 31);
    const lng =
      Math.abs(p.positionLong) <= 180
        ? p.positionLong
        : p.positionLong * (180 / 2 ** 31);
    if (isFinite(lat) && isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
      route.push([lat, lng]);
    }
  }
  return route;
}

export const STREAM_CHART_PATH_GAP_SEC = 30;
export function buildStreamMetricPath(
  stream: Array<{ timeSeconds: number }>,
  values: (number | null | undefined)[],
  toX: (index: number) => number,
  toY: (value: number) => number,
  options?: {
    startIdx?: number;
    endIdx?: number;
    maxGapSec?: number;
    /** Close each continuous segment to this baseline when drawing a filled area. */
    baselineY?: number;
  },
): string {
  const n = Math.min(stream.length, values.length);
  if (n === 0) return '';
  const start = Math.max(0, options?.startIdx ?? 0);
  const end = Math.min(n - 1, options?.endIdx ?? n - 1);
  const maxGapSec = options?.maxGapSec ?? STREAM_CHART_PATH_GAP_SEC;

  let d = '';
  let drawing = false;
  let firstX = '';
  let lastX = '';
  const closeSegment = () => {
    if (drawing && options?.baselineY != null) {
      const y = options.baselineY.toFixed(1);
      d += ` L${lastX},${y} L${firstX},${y} Z `;
    }
    drawing = false;
  };
  let prevTime: number | null = null;

  for (let i = start; i <= end; i++) {
    const v = values[i];
    const t = stream[i]?.timeSeconds;
    if (v == null || !Number.isFinite(v) || t == null || !Number.isFinite(t)) {
      closeSegment();
      prevTime = t != null && Number.isFinite(t) ? t : prevTime;
      continue;
    }
    if (prevTime != null && t - prevTime > maxGapSec) {
      closeSegment();
    }
    const x = toX(i).toFixed(1);
    const y = toY(v).toFixed(1);
    if (!drawing) firstX = x;
    lastX = x;
    d += drawing ? ` L${x},${y}` : `M${x},${y}`;
    drawing = true;
    prevTime = t;
  }

  closeSegment();
  return d.trim();
}

